import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getActiveJobs, getRecentJobs } from '../api/jobsApi';
import type { ActiveJob } from '../api/types';
import { subscribe, onReconnected, useWebSocket } from './useWebSocket';
import { useDismissedJobs } from './useDismissedJobs';

// 권위: docs/ARCH_DASHBOARD.md §4.0.3 (RealtimeWorkWidget) + §6.3.
// /topic/orchestration 구독 + /api/v1/jobs/active 초기 로드 + 재연결 시 재조회.
// 상태 머신: running → completed | failed | stale.

const STALE_MS = 2 * 60 * 60 * 1000; // 2시간

interface OrchestrationEvent {
  eventId?: string;
  eventCode?: string;
  event_code?: string;
  severity?: string;
  sourceAgent?: string;
  source_agent?: string;
  occurredAt?: string;
  occurred_at?: string;
  summary?: string;
  traceId?: string;
  trace_id?: string;
  entityRefs?: Record<string, unknown>;
  entity_refs?: Record<string, unknown>;
}

function pickCode(e: OrchestrationEvent): string {
  return e.eventCode ?? e.event_code ?? '';
}

function pickRefs(e: OrchestrationEvent): Record<string, unknown> {
  return (e.entityRefs ?? e.entity_refs ?? {}) as Record<string, unknown>;
}

function pickTraceId(e: OrchestrationEvent): string | undefined {
  return e.traceId ?? e.trace_id;
}

function pickOccurredAt(e: OrchestrationEvent): string {
  return e.occurredAt ?? e.occurred_at ?? new Date().toISOString();
}

export function deriveJobKey(e: OrchestrationEvent): { jobId: string; jobType: ActiveJob['jobType']; title: string } | null {
  const code = pickCode(e);
  if (!code) return null;
  const refs = pickRefs(e);

  if (code.startsWith('LEADING.PIPELINE.') || code.startsWith('LEADING.STEP')) {
    const runId = typeof refs.run_id === 'string' ? (refs.run_id as string) : typeof refs.runId === 'string' ? (refs.runId as string) : undefined;
    if (!runId) return null;
    return { jobId: runId, jobType: 'LEADING_PIPELINE', title: '주도주 배치' };
  }
  if (code.startsWith('DATA.CA_RESYNC.')) {
    const batchId = typeof refs.batch_id === 'string' ? (refs.batch_id as string) : typeof refs.batchId === 'string' ? (refs.batchId as string) : undefined;
    if (!batchId) return null;
    return { jobId: batchId, jobType: 'CA_RESYNC', title: 'CA 재동기화' };
  }
  if (code.startsWith('DATA.STOCK_MASTER.') || code.startsWith('DATA.CORP_CODE_MAPPING.')) {
    const batchId = typeof refs.batch_id === 'string' ? (refs.batch_id as string) : typeof refs.batchId === 'string' ? (refs.batchId as string) : undefined;
    if (!batchId) return null;
    return { jobId: batchId, jobType: 'STOCK_MASTER_BATCH', title: '종목 마스터 배치' };
  }
  if (code.startsWith('LLM.GATE_')) {
    const tid = pickTraceId(e);
    if (!tid) return null;
    const letter = code.charAt('LLM.GATE_'.length);
    return { jobId: tid, jobType: `LLM_GATE_${letter}` as ActiveJob['jobType'], title: `LLM 게이트 ${letter}` };
  }
  return null;
}

export function classifyStatus(code: string): 'started' | 'completed' | 'failed' | 'skipped' | 'stepStart' | 'stepComplete' | 'other' {
  if (!code) return 'other';
  // STEP 단계는 마침표 구분자만 사용 (LEADING.STEPn.STARTED / .COMPLETED)
  if (code.endsWith('.STARTED') && code.includes('STEP')) return 'stepStart';
  if ((code.endsWith('.COMPLETED') || code.endsWith('.DONE')) && code.includes('STEP')) return 'stepComplete';
  // 최종 상태 — 마침표(.) 또는 밑줄(_) 구분자 모두 허용 (GATE_E: BOOTSTRAP_STARTED / BOOTSTRAP_COMPLETED)
  if (code.endsWith('.STARTED') || code.endsWith('_STARTED')) return 'started';
  if (code.endsWith('.DONE') || code.endsWith('.COMPLETED') || code.endsWith('_COMPLETED')) return 'completed';
  if (code.endsWith('.FAILED') || code.endsWith('_FAILED') || code.endsWith('.SLA_BREACHED')) return 'failed';
  // 정상 조기 종료 — LEADING.PIPELINE.SKIPPED (유니버스 부족 등). terminal 로 취급.
  if (code.endsWith('.SKIPPED') || code.endsWith('_SKIPPED')) return 'skipped';
  return 'other';
}

function extractStepProgress(code: string): { currentStep: string; progress: number } | null {
  // LEADING.STEP2.STARTED / LEADING.STEP2.COMPLETED → STEP 2/5, 40%
  const m = code.match(/STEP(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  if (Number.isNaN(n)) return null;
  // Phase 3: 총 5단계 가정 (§4.0.3 LEADING.STEP 0~4)
  const total = 5;
  return { currentStep: `STEP ${n}/${total}`, progress: Math.round(((n + 1) / total) * 100) };
}

export interface RealtimeJobsState {
  activeJobs: ActiveJob[];
  recentJobs: ActiveJob[];
  isLoading: boolean;
  /** 현재 화면에 보이는 "종료된(완료/실패/지연)" 항목을 한꺼번에 dismiss. running 상태는 제외. */
  dismissFinished: () => void;
  /** 지정한 jobId 를 dismiss. running 상태인 경우는 호출부에서 가드 (여기서는 단순 위임). */
  dismissJob: (jobId: string) => void;
  /** 현재 "종료된 visible" 개수 — "모두 인지" 버튼 활성/비활성 결정용. */
  dismissibleCount: number;
}

/**
 * 초기 로드 + STOMP 실시간 반영.
 * 가독성 위해 reducer 대신 Map state + useRef 패턴 사용.
 */
export function useRealtimeJobs(): RealtimeJobsState {
  useWebSocket(); // 연결 보장

  const queryClient = useQueryClient();
  const { dismissedSet, dismiss, dismissMany } = useDismissedJobs();

  const activeQuery = useQuery({
    queryKey: ['jobs', 'active'],
    queryFn: getActiveJobs,
    staleTime: 30_000,
  });
  const recentQuery = useQuery({
    queryKey: ['jobs', 'recent'],
    queryFn: () => getRecentJobs(5),
    staleTime: 60_000,
  });

  // 실시간 덮어쓰기 레이어
  const [activeOverrides, setActiveOverrides] = useState<Map<string, ActiveJob>>(new Map());
  const [recentOverrides, setRecentOverrides] = useState<ActiveJob[]>([]);
  const activeRef = useRef(activeOverrides);
  activeRef.current = activeOverrides;

  // 클라이언트가 최종 상태(terminal: completed/failed/skipped) 이벤트를 받은 jobId 모음.
  // dashboard-api `/api/v1/jobs/active` 가 캐시/지연 등으로 같은 jobId 를 still running 으로 응답하더라도
  // WebSocket 이벤트가 권위(authoritative) — 화면 active 리스트에서 강제 제거한다.
  // 권위: docs/ARCH_DASHBOARD.md §4.0.3 (RealtimeWorkWidget — STARTED/COMPLETED 페어링).
  const [clientFinishedIds, setClientFinishedIds] = useState<Set<string>>(new Set());

  // STOMP 구독
  useEffect(() => {
    const handler = (msg: { body?: string }) => {
      if (!msg.body) return;
      let parsed: OrchestrationEvent;
      try {
        parsed = JSON.parse(msg.body) as OrchestrationEvent;
      } catch {
        return;
      }
      const key = deriveJobKey(parsed);
      if (!key) return;
      const code = pickCode(parsed);
      const kind = classifyStatus(code);
      const startedAt = pickOccurredAt(parsed);

      if (kind === 'started') {
        setActiveOverrides((prev) => {
          const next = new Map(prev);
          next.set(key.jobId, {
            jobId: key.jobId,
            jobType: key.jobType,
            title: key.title,
            startedAt,
            progress: null,
            currentStep: null,
            status: 'running',
            lastEventCode: code,
          });
          return next;
        });
      } else if (kind === 'stepStart' || kind === 'stepComplete') {
        const step = extractStepProgress(code);
        setActiveOverrides((prev) => {
          const existing = prev.get(key.jobId);
          if (!existing) return prev; // start 안 본 상태의 step 은 일단 무시
          const next = new Map(prev);
          next.set(key.jobId, {
            ...existing,
            currentStep: step?.currentStep ?? existing.currentStep,
            progress: step?.progress ?? existing.progress,
            lastEventCode: code,
          });
          return next;
        });
      } else if (kind === 'completed' || kind === 'failed' || kind === 'skipped') {
        setActiveOverrides((prev) => {
          const existing = prev.get(key.jobId);
          const next = new Map(prev);
          next.delete(key.jobId);
          const finished: ActiveJob = {
            jobId: key.jobId,
            jobType: key.jobType,
            title: key.title,
            startedAt: existing?.startedAt ?? startedAt,
            // skipped 는 진행률 개념이 없음 — 존재하면 유지, 없으면 null.
            progress:
              kind === 'completed'
                ? 100
                : kind === 'skipped'
                  ? existing?.progress ?? null
                  : existing?.progress ?? null,
            currentStep: existing?.currentStep ?? null,
            status: kind,
            lastEventCode: code,
          };
          // recentOverrides 에 prepend, 최대 5건
          setRecentOverrides((prevRecent) => [finished, ...prevRecent].slice(0, 5));
          return next;
        });
        // 서버 캐시가 늦더라도 active 에서 즉시 사라지도록 마킹.
        setClientFinishedIds((prev) => {
          if (prev.has(key.jobId)) return prev;
          const next = new Set(prev);
          next.add(key.jobId);
          return next;
        });
        // 서버 응답을 권위 있는 후속 상태로 재조회 (recent 갱신, active 정리).
        queryClient.invalidateQueries({ queryKey: ['jobs', 'active'] });
        queryClient.invalidateQueries({ queryKey: ['jobs', 'recent'] });
      }
    };

    const u1 = subscribe('/topic/orchestration', handler);
    // system-events 에서도 orchestration 관련 패턴만 필터링 수신 (보조 안정성)
    const u2 = subscribe('/topic/system-events', (msg) => {
      if (!msg.body) return;
      try {
        const parsed = JSON.parse(msg.body) as OrchestrationEvent;
        const code = pickCode(parsed);
        if (
          code.startsWith('LEADING.PIPELINE.') ||
          code.startsWith('LEADING.STEP') ||
          code.startsWith('DATA.CA_RESYNC.') ||
          code.startsWith('DATA.STOCK_MASTER.') ||
          code.startsWith('DATA.CORP_CODE_MAPPING.') ||
          code.startsWith('LLM.GATE_')
        ) {
          handler(msg);
        }
      } catch {
        /* ignore */
      }
    });
    return () => {
      u1();
      u2();
    };
    // queryClient 인스턴스는 Provider 내내 안정적이지만, 린트/명시성 차원에서 deps 에 포함.
  }, [queryClient]);

  // 재연결 시 /jobs/active 재조회
  useEffect(() => {
    const off = onReconnected(() => {
      queryClient.invalidateQueries({ queryKey: ['jobs', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['jobs', 'recent'] });
    });
    return off;
  }, [queryClient]);

  // stale 판정 (2시간 초과) + dismissed 필터링
  const merged = useMemo<Omit<RealtimeJobsState, 'dismissFinished' | 'dismissJob' | 'dismissibleCount'>>(() => {
    const fromServer = activeQuery.data?.jobs ?? [];
    const map = new Map<string, ActiveJob>();
    for (const j of fromServer) {
      // 클라이언트가 이미 terminal 이벤트를 본 jobId 는 서버 응답이 still running 이라도 무시.
      // (dashboard-api 가 COMPLETED 처리 누락한 경우의 self-heal.)
      if (clientFinishedIds.has(j.jobId)) continue;
      map.set(j.jobId, j);
    }
    for (const [id, j] of activeOverrides) map.set(id, j);

    const now = Date.now();
    const active: ActiveJob[] = Array.from(map.values()).map((j) => {
      if (j.status !== 'running') return j;
      const started = Date.parse(j.startedAt);
      if (!Number.isNaN(started) && now - started > STALE_MS) {
        return { ...j, status: 'stale' };
      }
      return j;
    });

    // dismissed 필터: 종료(running 외) 상태만 숨긴다. running 중인 작업은 강제로 가리지 않는다.
    const activeFiltered = active.filter((j) => {
      if (j.status === 'running') return true;
      return !dismissedSet.has(j.jobId);
    });

    const serverRecent = recentQuery.data?.jobs ?? [];
    // override 된 recent 가 앞에 오도록 dedupe
    const seen = new Set<string>();
    const recent: ActiveJob[] = [];
    for (const j of [...recentOverrides, ...serverRecent]) {
      if (seen.has(j.jobId)) continue;
      seen.add(j.jobId);
      // recent 는 전부 종료 상태이므로 dismissed 인 것은 완전히 숨김.
      if (dismissedSet.has(j.jobId)) continue;
      recent.push(j);
      if (recent.length >= 5) break;
    }

    return {
      activeJobs: activeFiltered.sort((a, b) => b.startedAt.localeCompare(a.startedAt)),
      recentJobs: recent,
      isLoading: activeQuery.isLoading,
    };
  }, [activeQuery.data, activeQuery.isLoading, activeOverrides, recentOverrides, recentQuery.data, dismissedSet, clientFinishedIds]);

  // "모두 인지" — 현재 화면에 보이는 종료 상태만 대상으로.
  const dismissFinished = useCallback(() => {
    const ids: string[] = [];
    for (const j of merged.activeJobs) {
      if (j.status !== 'running') ids.push(j.jobId);
    }
    for (const j of merged.recentJobs) {
      ids.push(j.jobId);
    }
    if (ids.length) dismissMany(ids);
  }, [merged.activeJobs, merged.recentJobs, dismissMany]);

  const dismissibleCount =
    merged.activeJobs.filter((j) => j.status !== 'running').length + merged.recentJobs.length;

  return {
    ...merged,
    dismissFinished,
    dismissJob: dismiss,
    dismissibleCount,
  };
}
